// src/components/partners/RetailerDetails.jsx
import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Table, Button, Alert, Spinner, Badge, Modal, Form } from 'react-bootstrap';
import { Edit, ArrowLeft, User, Mail, Phone, MapPin, FileText, DollarSign, Calendar, Clock, Users, BookOpen } from 'lucide-react';
import useAuth from '../../hooks/useAuth';
import { getRetailerById, assignRetailerToCommercial } from '../../api/partner';
import { getUsersByRole } from '../../api/users';

const RetailerDetails = ({ retailerId, onEdit, onBack }) => {
    const { isAdmin, isManager } = useAuth();
    const [retailer, setRetailer] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // États pour le modal d'assignation
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [loadingCommercials, setLoadingCommercials] = useState(false);
    const [commercials, setCommercials] = useState([]);
    const [selectedCommercialId, setSelectedCommercialId] = useState('');
    const [retailerToAssign, setRetailerToAssign] = useState(null);
    const [success, setSuccess] = useState('');
    const [alertError, setAlertError] = useState('');

    const canModify = isAdmin || isManager;
    const canAssign = isAdmin || isManager;

    useEffect(() => {
        const fetchRetailerDetails = async () => {
            try {
                setLoading(true);
                setError('');
                const data = await getRetailerById(retailerId);
                setRetailer(data);
            } catch (err) {
                setError('Impossible de charger les détails du détaillant. Veuillez réessayer.');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        if (retailerId) {
            fetchRetailerDetails();
        }
    }, [retailerId]);

    const fetchCommercials = async () => {
        try {
            setLoadingCommercials(true);
            const data = await getUsersByRole('ROLE_SALES_REP');
            setCommercials(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('Erreur lors du chargement des commerciaux', err);
        } finally {
            setLoadingCommercials(false);
        }
    };

    const openAssignModal = (retailer) => {
        setRetailerToAssign(retailer);
        setSelectedCommercialId(retailer.commercialUser ? retailer.commercialUser.id : '');
        setShowAssignModal(true);
        fetchCommercials(); // Charger les commerciaux lors de l'ouverture du modal
    };

    const handleAssignCommercial = async () => {
        if (!retailerToAssign) return;

        try {
            await assignRetailerToCommercial(retailerToAssign.id, selectedCommercialId);
            setShowAssignModal(false);

            // Recharger les détails du détaillant pour mettre à jour l'affichage
            const updatedData = await getRetailerById(retailerId);
            setRetailer(updatedData);

            // Afficher le message de succès
            setSuccess('Commercial assigné avec succès');
            setAlertError('');

            // Effacer le message après 5 secondes
            setTimeout(() => {
                setSuccess('');
            }, 5000);

        } catch (err) {
            setAlertError('Échec de l\'assignation du commercial. Veuillez réessayer.');
            setSuccess('');
            console.error(err);

            // Effacer le message d'erreur après 5 secondes
            setTimeout(() => {
                setAlertError('');
            }, 5000);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('fr-FR', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    };

    const getCommercialName = (commercial) => {
        if (!commercial) return '-';
        return commercial.fullName || ''
    };

    if (loading) {
        return (
            <Card className="shadow-sm">
                <Card.Body className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-3">Chargement des détails du détaillant...</p>
                </Card.Body>
            </Card>
        );
    }

    if (error) {
        return (
            <Card className="shadow-sm">
                <Card.Body>
                    <Alert variant="danger">{error}</Alert>
                    <div className="d-flex justify-content-center mt-3">
                        <Button variant="outline-secondary" onClick={onBack}>
                            <ArrowLeft size={16} className="me-1" />
                            Retour
                        </Button>
                    </div>
                </Card.Body>
            </Card>
        );
    }

    if (!retailer) {
        return (
            <Card className="shadow-sm">
                <Card.Body>
                    <Alert variant="warning">Aucun détaillant trouvé.</Alert>
                    <div className="d-flex justify-content-center mt-3">
                        <Button variant="outline-secondary" onClick={onBack}>
                            <ArrowLeft size={16} className="me-1" />
                            Retour
                        </Button>
                    </div>
                </Card.Body>
            </Card>
        );
    }

    return (
        <>
            <Card className="shadow-sm">
                {/* En-tête : empile titre puis boutons sous sm, ligne dès sm */}
                <Card.Header className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-2">
                    <h5 className="mb-0 d-flex align-items-center">
                        <User size={20} className="me-2 text-primary" />
                        Détails du détaillant
                    </h5>
                    <div className="d-flex gap-2 flex-wrap">
                        {canAssign && (
                            <Button
                                variant="outline-success"
                                size="sm"
                                onClick={() => openAssignModal(retailer)}
                            >
                                <Users size={16} className="me-1" />
                                Assigner un commercial
                            </Button>
                        )}
                        {canModify && (
                            <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => onEdit(retailerId)}
                            >
                                <Edit size={16} className="me-1" />
                                Modifier
                            </Button>
                        )}
                    </div>
                </Card.Header>
                <Card.Body>
                    {/* Alertes de succès et d'erreur */}
                    {success && (
                        <Alert variant="success" dismissible onClose={() => setSuccess('')}>
                            {success}
                        </Alert>
                    )}
                    {alertError && (
                        <Alert variant="danger" dismissible onClose={() => setAlertError('')}>
                            {alertError}
                        </Alert>
                    )}

                    <Row>
                        <Col md={6}>
                            <div className="mb-4">
                                <h4>{retailer.name}</h4>
                                <div className="mt-3">
                                    {retailer.businessType && (
                                        <p className="text-muted mb-2 d-flex align-items-center">
                                            <BookOpen size={16} className="me-2 flex-shrink-0" />
                                            Type d'activité: {retailer.businessType}
                                        </p>
                                    )}
                                    {retailer.email && (
                                        <p className="text-muted mb-2 d-flex align-items-center text-break">
                                            <Mail size={16} className="me-2 flex-shrink-0" />
                                            {retailer.email}
                                        </p>
                                    )}
                                    {retailer.phone && (
                                        <p className="text-muted mb-2 d-flex align-items-center">
                                            <Phone size={16} className="me-2 flex-shrink-0" />
                                            {retailer.phone}
                                        </p>
                                    )}
                                    {retailer.commercialUser && (
                                        <p className="text-muted mb-2 d-flex align-items-center">
                                            <Users size={16} className="me-2 flex-shrink-0" />
                                            Commercial: {getCommercialName(retailer.commercialUser)}
                                        </p>
                                    )}
                                </div>
                            </div>

                            {retailer.address && (
                                <div className="mb-4">
                                    <h6 className="mb-2 d-flex align-items-center">
                                        <MapPin size={16} className="me-2" />
                                        Adresse
                                    </h6>
                                    <div className="bg-light p-3 rounded">
                                        {retailer.address.split('\n').map((line, index) => (
                                            <p key={index} className={index !== 0 ? 'mt-1 mb-0' : 'mb-0'}>
                                                {line}
                                            </p>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="mt-4">
                                <div className="d-flex flex-wrap gap-2">
                                    <Badge bg={retailer.vatApplicable ? 'success' : 'secondary'} className="p-2">
                                        {retailer.vatApplicable ? 'TVA applicable' : 'Non soumis à TVA'}
                                    </Badge>

                                    {retailer.creditLimit && (
                                        <Badge bg="info" className="p-2">
                                            Limite de crédit: {retailer.creditLimit.toFixed(2)}
                                        </Badge>
                                    )}
                                </div>
                            </div>
                        </Col>

                        <Col md={6}>
                            <Table bordered responsive size="sm" className="mt-md-0 mt-3">
                                <tbody>
                                    <tr>
                                        <td className="bg-light" width="40%">
                                            <User size={14} className="me-1" /> Type de partenaire
                                        </td>
                                        <td>
                                            <Badge bg="primary">Détaillant</Badge>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td className="bg-light">
                                            <Users size={14} className="me-1" /> Commercial
                                        </td>
                                        <td>{retailer.commercialUser ? getCommercialName(retailer.commercialUser) : '-'}</td>
                                    </tr>
                                    <tr>
                                        <td className="bg-light">
                                            <Calendar size={14} className="me-1" /> Créé le
                                        </td>
                                        <td>{formatDate(retailer.createdAt)}</td>
                                    </tr>
                                    <tr>
                                        <td className="bg-light">
                                            <Clock size={14} className="me-1" /> Dernière mise à jour
                                        </td>
                                        <td>{formatDate(retailer.updatedAt)}</td>
                                    </tr>
                                </tbody>
                            </Table>

                            {retailer.notes && (
                                <div className="mt-4">
                                    <h6 className="mb-2">Notes</h6>
                                    <div className="bg-light p-3 rounded">
                                        {retailer.notes.split('\n').map((line, index) => (
                                            <p key={index} className={index !== 0 ? 'mt-2 mb-0' : 'mb-0'}>
                                                {line}
                                            </p>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </Col>
                    </Row>

                    <div className="d-flex justify-content-start mt-4">
                        <Button variant="outline-secondary" onClick={onBack}>
                            <ArrowLeft size={16} className="me-1" />
                            Retour à la liste
                        </Button>
                    </div>
                </Card.Body>
            </Card>

            {/* Modal d'assignation de commercial */}
            <Modal
                show={showAssignModal}
                onHide={() => setShowAssignModal(false)}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Assigner un commercial</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {loadingCommercials ? (
                        <div className="text-center py-3">
                            <div className="spinner-border spinner-border-sm text-primary" role="status">
                                <span className="visually-hidden">Chargement...</span>
                            </div>
                            <p className="mt-2">Chargement des commerciaux...</p>
                        </div>
                    ) : (
                        <>
                            <p>
                                <strong>Détaillant :</strong> {retailerToAssign?.name}
                            </p>
                            <Form.Group className="mb-3">
                                <Form.Label>Commercial</Form.Label>
                                <Form.Select
                                    name="commercialUser"
                                    value={selectedCommercialId}
                                    onChange={(e) => setSelectedCommercialId(e.target.value)}
                                >
                                    <option value="">-- Aucun commercial--</option>
                                    {commercials.map(commercial => (
                                        <option key={commercial.id} value={commercial.id}>
                                            {getCommercialName(commercial)}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowAssignModal(false)}>
                        Annuler
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleAssignCommercial}
                        disabled={loadingCommercials}
                    >
                        Assigner
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default RetailerDetails;